import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthenticatedApiClient, QUERY_KEYS } from '../client';
import {
  CreateMaterialContainersRequest,
  CreateMaterialContainersResponse,
  GetMaterialContainerByCodeResponse,
  GetLastUsedLotForMaterialResponse,
  CreateMaterialContainerItem,
  ListMaterialContainersResponse,
  PrintMaterialContainerLabelsRequest,
  PrintMaterialContainerLabelsResponse,
  PrintLotLabelsRequest,
  PrintLotLabelsResponse,
  PrintLotCalibrationLabelRequest,
  PrintLotCalibrationLabelResponse,
  FeedLotMediaRequest,
  FeedLotMediaResponse,
  GetLotLabelCalibrationResponse,
  SetLotLabelCalibrationRequest,
  SetLotLabelCalibrationResponse,
  NudgeLotLabelCalibrationRequest,
  NudgeLotLabelCalibrationResponse,
  LabelDriftDirection,
  LabelDriftSpeed,
} from '../generated/api-client';

const LOT_LABEL_CALIBRATION_QUERY_KEY = [...QUERY_KEYS.materialContainers, 'lot-label-calibration'];

export const useCreateMaterialContainers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { items: CreateMaterialContainerItem[] }): Promise<CreateMaterialContainersResponse> => {
      const apiClient = getAuthenticatedApiClient();
      const request = new CreateMaterialContainersRequest({ items: input.items });
      return apiClient.materialContainers_Create(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.materialContainers });
    },
  });
};

export const usePrintMaterialContainerLabels = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { count: number; mediaChangeConfirmed?: boolean }): Promise<PrintMaterialContainerLabelsResponse> => {
      const apiClient = getAuthenticatedApiClient();
      const request = new PrintMaterialContainerLabelsRequest({
        count: input.count,
        mediaChangeConfirmed: input.mediaChangeConfirmed ?? false,
      });
      return apiClient.materialContainers_PrintLabels(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.materialContainers });
    },
  });
};

export const usePrintLotLabels = () =>
  useMutation({
    mutationFn: (input: {
      lotNumber: string;
      expiration: string;
      count: number;
      mediaChangeConfirmed?: boolean;
    }): Promise<PrintLotLabelsResponse> => {
      const apiClient = getAuthenticatedApiClient();
      const request = new PrintLotLabelsRequest({
        lotNumber: input.lotNumber,
        expiration: input.expiration,
        count: input.count,
        mediaChangeConfirmed: input.mediaChangeConfirmed ?? false,
      });
      return apiClient.lots_PrintLabels(request);
    },
  });

export const usePrintLotCalibrationLabel = () =>
  useMutation({
    mutationFn: (input?: { mediaChangeConfirmed?: boolean }): Promise<PrintLotCalibrationLabelResponse> => {
      const apiClient = getAuthenticatedApiClient();
      const request = new PrintLotCalibrationLabelRequest({
        mediaChangeConfirmed: input?.mediaChangeConfirmed ?? false,
      });
      return apiClient.lots_PrintCalibrationLabel(request);
    },
  });

export const useFeedLotMedia = () =>
  useMutation({
    mutationFn: (input: { dots: number; mediaChangeConfirmed?: boolean }): Promise<FeedLotMediaResponse> => {
      const apiClient = getAuthenticatedApiClient();
      const request = new FeedLotMediaRequest({
        dots: input.dots,
        mediaChangeConfirmed: input.mediaChangeConfirmed ?? false,
      });
      return apiClient.lots_FeedMedia(request);
    },
  });

// The calibration drives every printed label, so it must never be served from cache:
// the app-wide staleTime would otherwise show a pitch/drift the printer no longer uses
// after someone else (or another tab) changed it.
//
// refetchOnMount: 'always' is what does the work here. The only caller is the calibration
// tab, which mounts on demand when the operator selects it, so every visit refetches.
// staleTime: 0 still covers a caller that keeps the hook mounted and toggles `enabled`,
// and gcTime: 0 keeps no stale value around for the next consumer to read.
// refetchOnWindowFocus is spelled out even though it matches the current global default:
// this hook's freshness guarantee must not silently disappear if that default changes.
export const useLotLabelCalibration = (enabled: boolean) =>
  useQuery({
    enabled,
    queryKey: LOT_LABEL_CALIBRATION_QUERY_KEY,
    queryFn: (): Promise<GetLotLabelCalibrationResponse> => {
      const apiClient = getAuthenticatedApiClient();
      return apiClient.lots_GetLabelCalibration();
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

export const useSetLotLabelCalibration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      pitchDots: number;
      driftDotsPer100Labels: number;
    }): Promise<SetLotLabelCalibrationResponse> => {
      const apiClient = getAuthenticatedApiClient();
      const request = new SetLotLabelCalibrationRequest(input);
      return apiClient.lots_SetLabelCalibration(request);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: LOT_LABEL_CALIBRATION_QUERY_KEY }),
  });
};

// The operator-facing calibration wizard. It sends only an observation — which way the
// printed text drifts and how fast — and the server derives, clamps and persists the
// correction, so no dot value ever crosses the wire and no calibration permission is
// needed. The advanced pitch/drift fields read the same record, hence the invalidation.
export const useNudgeLotLabelCalibration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      direction: LabelDriftDirection;
      speed: LabelDriftSpeed;
    }): Promise<NudgeLotLabelCalibrationResponse> => {
      const apiClient = getAuthenticatedApiClient();
      const request = new NudgeLotLabelCalibrationRequest(input);
      return apiClient.lots_NudgeLabelCalibration(request);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: LOT_LABEL_CALIBRATION_QUERY_KEY }),
  });
};

export const useMaterialContainerByCode = (code: string | null) =>
  useQuery({
    enabled: !!code,
    queryKey: [...QUERY_KEYS.materialContainers, 'by-code', code],
    queryFn: (): Promise<GetMaterialContainerByCodeResponse> => {
      const apiClient = getAuthenticatedApiClient();
      return apiClient.materialContainers_GetByCode(code!);
    },
  });

export const useLastUsedLotForMaterial = (materialCode: string | null) =>
  useQuery({
    enabled: !!materialCode,
    queryKey: [...QUERY_KEYS.materialContainers, 'last-used-lot', materialCode],
    queryFn: (): Promise<GetLastUsedLotForMaterialResponse> => {
      const apiClient = getAuthenticatedApiClient();
      return apiClient.materialContainers_GetLastUsedLot(materialCode!);
    },
  });

export interface MaterialContainersListRequest {
  materialCode?: string;
  lotCode?: string;
  code?: string;
  page?: number;
  pageSize?: number;
}

export const useMaterialContainersList = (request: MaterialContainersListRequest) =>
  useQuery({
    queryKey: [...QUERY_KEYS.materialContainers, 'list', request],
    queryFn: (): Promise<ListMaterialContainersResponse> => {
      const apiClient = getAuthenticatedApiClient();
      return apiClient.materialContainers_GetMaterialContainers(
        request.materialCode || undefined,
        request.lotCode || undefined,
        request.code || undefined,
        request.page ?? 1,
        request.pageSize ?? 20,
      );
    },
  });

export type {
  CreateMaterialContainerItem,
  CreateMaterialContainersResponse,
  GetMaterialContainerByCodeResponse,
  GetLastUsedLotForMaterialResponse,
  ListMaterialContainersResponse,
  PrintMaterialContainerLabelsResponse,
  PrintLotLabelsResponse,
} from '../generated/api-client';
